import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type Appointment, type AppointmentDoctor } from '@/api';
import { AppointmentStatusBadge } from './AppointmentStatusBadge';
import { Button } from '@/components/ui/Button';
import { PixQRCode } from '@/components/payments/PixQRCode';
import { useAuth } from '@/hooks';
import { paymentsApi } from '@/api/payments-api';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, CreditCard } from 'lucide-react';

interface AppointmentDetailsProps {
  appointment: Appointment;
  userRole: 'patient' | 'doctor';
  onCancel?: () => void;
  onReschedule?: () => void;
  onJoin?: () => void;
  onRefetch?: () => void;
  isLoading?: boolean;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatTime(dateString: string): string {
  return new Date(dateString).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

const CANCELLABLE_STATUSES = ['CREATED', 'WAITING_DOCTOR', 'DOCTOR_ACCEPTED', 'SCHEDULED', 'CONFIRMED'];
const RESCHEDULABLE_STATUSES = ['CREATED', 'WAITING_DOCTOR', 'DOCTOR_ACCEPTED', 'SCHEDULED', 'CONFIRMED'];
const JOINABLE_STATUSES = ['DOCTOR_ACCEPTED', 'CONFIRMED', 'SCHEDULED', 'IN_PROGRESS', 'READY', 'ACCEPTED', 'COMPLETED'];
const PAYABLE_STATUSES = ['CREATED', 'WAITING_PAYMENT'];

export function AppointmentDetails({
  appointment,
  userRole,
  onCancel,
  onReschedule,
  onJoin,
  onRefetch,
  isLoading = false,
}: AppointmentDetailsProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const person = userRole === 'patient' ? appointment.doctor : appointment.patient;
  const doctor = appointment.doctor;
  const patient = appointment.patient;

  const canCancel = CANCELLABLE_STATUSES.includes(appointment.status);
  const canReschedule = RESCHEDULABLE_STATUSES.includes(appointment.status);
  const isTelemed = (appointment as any).type !== 'IN_PERSON' && (appointment as any).type !== 'HOME_CARE' && (appointment as any).type !== 'PRESENCIAL';
  const canJoin = isTelemed && (JOINABLE_STATUSES.includes(appointment.status) || Boolean((appointment as any).videoRoomId) || Boolean((appointment as any).canJoin));
  const canPay =
    userRole === 'patient' &&
    PAYABLE_STATUSES.includes(appointment.status) &&
    Number(appointment.price ?? 0) > 0;

  const [showPayment, setShowPayment] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [pixData, setPixData] = useState<{
    paymentId: string;
    qrCode?: string;
    qrCodeUrl?: string;
    copyPaste?: string;
  } | null>(null);
  const paymentPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (paymentPollingRef.current) {
        clearInterval(paymentPollingRef.current);
        paymentPollingRef.current = null;
      }
    };
  }, []);

  const startPaymentPolling = (paymentId: string) => {
    if (paymentPollingRef.current) {
      clearInterval(paymentPollingRef.current);
    }
    paymentPollingRef.current = setInterval(async () => {
      try {
        const status = await paymentsApi.getPaymentStatus(paymentId);
        if (status.status === 'APPROVED' || status.status === 'PAID') {
          if (paymentPollingRef.current) {
            clearInterval(paymentPollingRef.current);
            paymentPollingRef.current = null;
          }
          queryClient.invalidateQueries({ queryKey: ['payments'] });
          queryClient.invalidateQueries({ queryKey: ['appointments'] });
          if (onRefetch) onRefetch();
          setShowPayment(false);
          setPixData(null);
        }
      } catch {
        // keep polling on transient failures
      }
    }, 4000);
  };

  const handlePayNow = async () => {
    if (!user || !doctor) return;

    setPaymentError(null);
    setShowPayment(true);
    setPaymentProcessing(true);

    try {
      const pixRes = await paymentsApi.createPixPayment({
        appointmentId: appointment.id,
        patientId: user.id,
        doctorId: doctor.id,
        amount: Number(appointment.price ?? 0),
        description: `Consulta com ${doctor.name || 'Médico'}`,
        patientName: user.name || user.email || 'Paciente',
        patientEmail: user.email || 'paciente@medikei.com.br',
        returnUrl: `${window.location.origin}/agendamentos/${appointment.id}`,
      });

      setPixData({
        paymentId: pixRes.paymentId,
        qrCode: pixRes.qrCode,
        qrCodeUrl: pixRes.qrCodeUrl,
        copyPaste: pixRes.qrCode || pixRes.checkoutUrl,
      });

      startPaymentPolling(pixRes.paymentId);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Não foi possível iniciar o pagamento. Tente novamente.';
      setPaymentError(message);
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handlePayWithCard = async () => {
    if (!user || !doctor) return;

    setPaymentError(null);
    setShowPayment(true);
    setPaymentProcessing(true);

    try {
      const cardRes = await paymentsApi.createCardPayment({
        appointmentId: appointment.id,
        patientId: user.id,
        doctorId: doctor.id,
        amount: Number(appointment.price ?? 0),
        description: `Consulta com ${doctor.name || 'Médico'}`,
        patientName: user.name || user.email || 'Paciente',
        patientEmail: user.email || 'paciente@medikei.com.br',
        installments: 1,
      });

      if (cardRes?.paymentId) {
        await paymentsApi.confirmPayment(cardRes.paymentId).catch(() => {});
      }

      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['appointments'] });
      if (onRefetch) onRefetch();
      setShowPayment(false);
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Não foi possível iniciar o pagamento. Tente novamente.';
      setPaymentError(message);
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleJoin = () => {
    if (onJoin) {
      onJoin();
    } else {
      if (userRole === 'doctor') {
        navigate(`/medico/telemedicine/session/${appointment.id}`);
      } else {
        navigate(`/telemedicine/waiting/${appointment.id}`);
      }
    }
  };

  const personSpecialty = person && 'specialty' in person ? (person as AppointmentDoctor).specialty : undefined;

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-6">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
              {person?.photoUrl ? (
                <img src={person.photoUrl} alt={person?.name || ''} className="w-full h-full object-cover" />
              ) : (
                <span className="text-2xl font-medium text-gray-500">
                  {person?.name?.charAt(0).toUpperCase() || '?'}
                </span>
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{person?.name}</h2>
              {personSpecialty && (
                <p className="text-gray-500">{personSpecialty}</p>
              )}
            </div>
          </div>
          <AppointmentStatusBadge status={appointment.status} size="lg" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Data e horário</h3>
              <p className="text-gray-900">{formatDate(appointment.scheduledAt)}</p>
              <p className="text-gray-700 font-medium">
                {formatTime(appointment.scheduledAt)} ({appointment.duration} min)
              </p>
            </div>

            {Number(appointment.price ?? 0) > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Valor</h3>
                <p className="text-gray-900">
                  {appointment.currency === 'BRL' ? 'R$ ' : ''}
                  {Number(appointment.price).toFixed(2).replace('.', ',')}
                </p>
              </div>
            )}

            {appointment.type && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Tipo</h3>
                <p className="text-gray-900">{appointment.type}</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            {appointment.reason && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Motivo da consulta</h3>
                <p className="text-gray-900">{appointment.reason}</p>
              </div>
            )}

            {appointment.notes && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Observações</h3>
                <p className="text-gray-900">{appointment.notes}</p>
              </div>
            )}

            {userRole === 'patient' && doctor && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Médico</h3>
                <Link
                  to={`/medicos/${doctor.id}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  Ver perfil
                </Link>
              </div>
            )}

            {userRole === 'doctor' && patient && (
              <div>
                <h3 className="text-sm font-medium text-gray-500">Paciente</h3>
                <Link
                  to={`/pacientes/${patient.id}/prontuarios`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  Ver perfil
                </Link>
              </div>
            )}
          </div>
        </div>

        {appointment.reminders && appointment.reminders.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Lembretes</h3>
            <div className="space-y-2">
              {appointment.reminders.map((reminder) => (
                <div
                  key={reminder.id}
                  className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-700">{reminder.type}</p>
                    <p className="text-xs text-gray-500">
                      Agendado para: {new Date(reminder.scheduledFor).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    reminder.status === 'SENT'
                      ? 'bg-green-100 text-green-800'
                      : reminder.status === 'PENDING'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {reminder.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {canPay && showPayment && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Pagamento via PIX</h3>
            {paymentError && (
              <div className="mb-3 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                {paymentError}
              </div>
            )}
            {paymentProcessing && !pixData && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            )}
            {pixData && (
              <div className="flex flex-col items-center gap-3">
                <PixQRCode
                  qrCode={pixData.qrCode}
                  qrCodeUrl={pixData.qrCodeUrl}
                  copyPaste={pixData.copyPaste}
                />
                <p className="text-xs text-muted-foreground text-center">
                  Após o pagamento, esta tela será atualizada automaticamente.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {(canCancel || canReschedule || canJoin || canPay) && (
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-wrap gap-3">
          {canPay && !showPayment && (
            <>
              <Button className="flex-1" onClick={handlePayNow} disabled={isLoading || paymentProcessing}>
                Pagar agora
              </Button>
              <Button variant="outline" onClick={handlePayWithCard} disabled={isLoading || paymentProcessing}>
                <CreditCard className="h-4 w-4 mr-1" />
                Cartão
              </Button>
            </>
          )}
          {canJoin && (
            <Button className="flex-1" onClick={handleJoin} isLoading={isLoading}>
              Entrar na consulta
            </Button>
          )}
          {canReschedule && (
            <Button variant="outline" onClick={onReschedule} disabled={isLoading}>
              Reagendar
            </Button>
          )}
          {canCancel && (
            <Button variant="ghost" onClick={onCancel} disabled={isLoading}>
              Cancelar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
